import React from 'react';
import { PinOnImage, type PinOnImageResultT } from '@mentimeter/visualizations';
import { usePrevious } from '@mentimeter/react-hooks';
import {
  SvgElement,
  VisualizationUpdater,
} from '@mentimeter/question-modules-shared';
import { PlaceholderImage } from './PlaceholderImage';

const getFormattedCorrectArea = (
  correctArea: {
    top: number;
    left: number;
    right: number;
    bottom: number;
  } | null,
) => {
  if (!correctArea) {
    return null;
  }
  const { top, left, right, bottom } = correctArea;
  const isReset = top === 0 && left === 0 && right === 1 && bottom === 1;
  return isReset
    ? null
    : [
        [left, top],
        [right, top],
        [right, bottom],
        [left, bottom],
        [left, top],
      ];
};

interface Props {
  selector: string;
  results: PinOnImageResultT[];
  imageUrl?: string | undefined | null;
  color: string;
  textColor: string;
  placeholderImageColor: string;
  showCorrect: boolean;
  showCorrectEnabled: boolean;
  correctArea: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  } | null;
  animationMultiplier: 1 | 0;
  imageSize: { width: number; height: number };
}

export const PinOnImageVisualization = React.memo<Props>(
  ({
    selector,
    results,
    imageUrl,
    color,
    showCorrect,
    showCorrectEnabled,
    textColor,
    placeholderImageColor,
    correctArea,
    animationMultiplier,
    imageSize,
  }) => {
    const formattedCorrectArea = getFormattedCorrectArea(correctArea);

    const prevShowCorrect = usePrevious(showCorrect, false);

    const pinOnImage = new PinOnImage({
      selector: `#${selector}`,
      results,
      imageSize,
      showCorrect,
      prevShowCorrect,
      showCorrectEnabled,
      color,
      textColor,
      correctArea: formattedCorrectArea,
      animationMultiplier,
    });

    if (imageSize.width === 0) return null;

    return (
      <SvgElement viewBox={`0 0 ${imageSize.width} ${imageSize.height}`}>
        <VisualizationUpdater updateFn={pinOnImage.update} />
        {imageUrl ? (
          <image
            href={imageUrl}
            width={imageSize.width}
            height={imageSize.height}
          />
        ) : (
          <PlaceholderImage color={placeholderImageColor} />
        )}
        <g id={selector} />
      </SvgElement>
    );
  },
);
